import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

/** What each Replo plan sees in Sightglass. The server enforces it; the miniapp only reads it. */

export const sightglassPlanSchema = z
  .enum(["free", "starter", "pro"])
  .describe("SightglassPlan");
export type SightglassPlan = z.infer<typeof sightglassPlanSchema>;

export const sightglassLockSchema = z
  .enum(["estimates", "history", "deepCharts"])
  .describe(
    "SightglassLock: a class of data the caller's plan withholds. `estimates` = sales, visits and traffic; `history` = trend beyond the plan's window; `deepCharts` = launches, longevity and the momentum breakdown.",
  );
export type SightglassLock = z.infer<typeof sightglassLockSchema>;

export const sightglassLimitsSchema = z
  .object({
    estimates: z.boolean(),
    rowCaps: z
      .object({
        ads: z.number().int(),
        sitePages: z.number().int(),
        companies: z.number().int(),
      })
      .nullable()
      .describe(
        "Rows a plan sees per list, newest or top first; null = every row.",
      ),
    historyDays: z
      .number()
      .int()
      .nullable()
      .describe("Ads active within this many days; null = no window."),
    trendDays: z
      .number()
      .int()
      .nullable()
      .describe("Longest company trend; 0 = locked, null = no limit."),
    deepCharts: z.boolean(),
    researchPerMonth: z
      .number()
      .int()
      .nullable()
      .describe(
        "Companies new to Sightglass researched per calendar month; null = unlimited.",
      ),
  })
  .describe("SightglassLimits");
export type SightglassLimits = z.infer<typeof sightglassLimitsSchema>;

export const SIGHTGLASS_PLAN_LIMITS: Record<SightglassPlan, SightglassLimits> =
  {
    free: {
      estimates: false,
      rowCaps: { ads: 8, sitePages: 4, companies: 10 },
      historyDays: 90,
      trendDays: 0,
      deepCharts: false,
      researchPerMonth: 1,
    },
    starter: {
      estimates: true,
      rowCaps: null,
      historyDays: 90,
      trendDays: 90,
      deepCharts: false,
      researchPerMonth: 25,
    },
    pro: {
      estimates: true,
      rowCaps: null,
      historyDays: null,
      trendDays: 365,
      deepCharts: true,
      researchPerMonth: 100,
    },
  };

/** Enforcement switched off: today's behaviour, nothing withheld. */
export const SIGHTGLASS_UNRESTRICTED_LIMITS: SightglassLimits = {
  estimates: true,
  rowCaps: null,
  historyDays: null,
  trendDays: null,
  deepCharts: true,
  researchPerMonth: null,
};

export const sightglassAccessWireSchema = z
  .object({
    plan: sightglassPlanSchema,
    limits: sightglassLimitsSchema,
    research: z.object({
      usedThisMonth: z
        .number()
        .int()
        .describe("New companies researched this calendar month."),
    }),
  })
  .describe("SightglassAccessWire");
export type SightglassAccessWire = z.infer<typeof sightglassAccessWireSchema>;

/** Superuser-only: forces a plan for QA. Anyone else sending it is ignored. */
export const SIGHTGLASS_PLAN_OVERRIDE_HEADER =
  "X-Replo-Sightglass-Plan-Override";
/** The project the miniapp is open in; the server checks membership before trusting it. */
export const SIGHTGLASS_PROJECT_HEADER = "X-Project-Id";

/** The plan an upgrade from `plan` should target; null at the top. */
export function nextSightglassPlan(
  plan: SightglassPlan,
): "starter" | "pro" | null {
  return exhaustiveSwitch(plan)<"starter" | "pro" | null>({
    free: "starter",
    starter: "pro",
    pro: null,
  });
}
